/**
 * Per-engine guest RTCStats capability matrix.
 *
 * Safari (every iPhone/iPad) and Android do not expose the same RTCStats fields Chromium
 * does, and the measurement contract (clientStreamStatsContracts.ts) is omit-don't-zero-fill:
 * a field the engine cannot sample must be omitted, never fabricated. This matrix is the
 * single source of truth the guest sampler and the harness both read to know which signals
 * are real on a given engine.
 *
 * Browser-free and Node/Jest-safe: pure data + one lookup function.
 */

import type { GuestBrowserEngine } from './adverseNetworkContract';

export type GuestBrowserCapability = {
  engine: GuestBrowserEngine;
  /** inbound-rtp freezeCount. Absent on Safari. */
  exposesFreezeCount: boolean;
  /** inbound-rtp framesReceived (decode-backlog input). Absent on Safari. */
  exposesFramesReceived: boolean;
  /** jitterBufferDelay / jitterBufferEmittedCount. Absent on Safari. */
  exposesJitterBufferDelay: boolean;
  /**
   * True when jitterBufferTarget is pinned to 0 (Android low-latency), so RTT and JB stay flat
   * while a decoder falls behind — playout-latency trend is the only decode-side signal.
   */
  jitterBufferPinned: boolean;
  /** True when the engine offers constrained-baseline H.264 only (Safari-class). */
  constrainedBaselineOnlyH264: boolean;
  /**
   * A resolution change reinitialises the hardware decoder (Android MediaCodec, WebKit
   * VideoToolbox), so a resolution downgrade taken to cure a standing buffer adds a
   * reconfigure hitch and deepens the buffer instead. Desktop Chromium re-uses its decoder.
   */
  resolutionChangeReconfiguresDecoder: boolean;
  /** inbound-rtp jitterBufferTargetDelay / jitterBufferMinimumDelay present. Absent on Safari. */
  exposesJitterBufferTargetDelay: boolean;
  /**
   * A keyframe inflates this engine's jitter-buffer target for minutes. libwebrtc's jitter
   * estimator sizes the buffer from `(max_frame_size - avg_frame_size) / channel_rate`, where
   * `max_frame_size` is a peak tracker decaying at ~0.9999 per frame; an IDR is the largest
   * frame the stream ever carries. On Android the WiFi driver's packet aggregation also makes
   * a large frame land as a burst tail, which the filter reads as a slow channel and multiplies
   * every size delta by. Field shape 2026-09-20: target ratcheted 80 -> 271 ms on a 6 ms RTT
   * and never came down. Desktop Chromium runs the same estimator but its channel term stays
   * near zero on a wired link, so the effect has not shown; Safari does not use this estimator.
   * When true, the host withholds every keyframe a decodability requirement does not stand behind.
   */
  keyframeInflatesJitterBuffer: boolean;
};

/** Which rVFC metadata keys a guest actually populates (Phase 5 capability probe). */
export type GuestLatencyCapability = {
  hasRtpTimestamp: boolean;
  hasCaptureTime: boolean;
  hasReceiveTime: boolean;
  hasProcessingDuration: boolean;
};

const CAPABILITIES: Record<GuestBrowserEngine, GuestBrowserCapability> = {
  'safari-ios': {
    engine: 'safari-ios',
    exposesFreezeCount: false,
    exposesFramesReceived: false,
    exposesJitterBufferDelay: false,
    jitterBufferPinned: false,
    constrainedBaselineOnlyH264: true,
    resolutionChangeReconfiguresDecoder: true,
    exposesJitterBufferTargetDelay: false,
    keyframeInflatesJitterBuffer: false,
  },
  'android-chrome': {
    engine: 'android-chrome',
    exposesFreezeCount: true,
    exposesFramesReceived: true,
    exposesJitterBufferDelay: true,
    jitterBufferPinned: true,
    constrainedBaselineOnlyH264: false,
    resolutionChangeReconfiguresDecoder: true,
    exposesJitterBufferTargetDelay: true,
    keyframeInflatesJitterBuffer: true,
  },
  'desktop-chrome': {
    engine: 'desktop-chrome',
    exposesFreezeCount: true,
    exposesFramesReceived: true,
    exposesJitterBufferDelay: true,
    jitterBufferPinned: false,
    constrainedBaselineOnlyH264: false,
    resolutionChangeReconfiguresDecoder: false,
    exposesJitterBufferTargetDelay: true,
    keyframeInflatesJitterBuffer: false,
  },
};

/** The capability flags for a guest engine. */
export function capabilityForEngine(engine: GuestBrowserEngine): GuestBrowserCapability {
  return CAPABILITIES[engine];
}

/** Probe which rVFC metadata keys are present on one observed frame. */
export function probeGuestLatencyCapability(meta: {
  rtpTimestamp?: unknown;
  captureTime?: unknown;
  receiveTime?: unknown;
  processingDuration?: unknown;
}): GuestLatencyCapability {
  const finite = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
  return {
    hasRtpTimestamp: finite(meta.rtpTimestamp),
    hasCaptureTime: finite(meta.captureTime),
    hasReceiveTime: finite(meta.receiveTime),
    hasProcessingDuration: finite(meta.processingDuration),
  };
}
